// Region table for multi-region online play. Reads URLs from VITE env vars
// at build time. If at least one regional URL is set, those are the live
// regions and VITE_COLYSEUS_URL is ignored. Otherwise (local dev with no
// regional URLs), VITE_COLYSEUS_URL is exposed as a single "Local" region.

export interface Region {
  /** Stable short id used in URL params and localStorage. */
  id: string;
  /** Human-readable label for the region picker. */
  label: string;
  /** Colyseus WebSocket URL (ws:// or wss://). */
  url: string;
}

interface RegionEnv {
  VITE_COLYSEUS_URL?: string;
  VITE_COLYSEUS_URL_EU?: string;
  VITE_COLYSEUS_URL_US_EAST?: string;
  VITE_COLYSEUS_URL_US_WEST?: string;
  VITE_COLYSEUS_URL_ASIA?: string;
  VITE_COLYSEUS_URL_AU?: string;
}

const env = (import.meta.env as unknown as RegionEnv);

const SPEC: Array<{ id: string; label: string; envKey: keyof RegionEnv }> = [
  { id: 'eu',      label: 'EU (London)', envKey: 'VITE_COLYSEUS_URL_EU' },
  { id: 'us-east', label: 'US East',     envKey: 'VITE_COLYSEUS_URL_US_EAST' },
  { id: 'us-west', label: 'US West',     envKey: 'VITE_COLYSEUS_URL_US_WEST' },
  { id: 'asia',    label: 'Asia',        envKey: 'VITE_COLYSEUS_URL_ASIA' },
  { id: 'au',      label: 'Australia',   envKey: 'VITE_COLYSEUS_URL_AU' }
];

export const REGIONS: ReadonlyArray<Region> = (() => {
  const present = SPEC
    .map((s) => ({ id: s.id, label: s.label, url: (env[s.envKey] ?? '').trim() }))
    .filter((r) => r.url.length > 0);
  if (present.length > 0) return present;
  // Local-dev fallback: VITE_COLYSEUS_URL becomes the sole region.
  const local = (env.VITE_COLYSEUS_URL ?? 'ws://localhost:2567').trim();
  return [{ id: 'local', label: 'Local', url: local }];
})();

export function getRegionById(id: string | null | undefined): Region | null {
  if (!id) return null;
  return REGIONS.find((r) => r.id === id) ?? null;
}

export function getDefaultRegion(): Region {
  return REGIONS[0]!;
}

/** True when the live set is the local-dev fallback (one region with id 'local'). */
export function isLocalOnly(): boolean {
  return REGIONS.length === 1 && REGIONS[0]!.id === 'local';
}
